import { UnreachableCaseError } from 'ts-essentials';
import {
  FieldValueType,
  FilterType,
  IBasicFilterInput,
  IFilterFieldsFragment,
  IFilterInput,
  ILogicalOperator,
  IPredicateFieldsFragment,
  IPredicateInput,
  ISimplePredicateFieldsFragment,
  LogicalOperatorKey,
  OperandType,
} from './apollo/graphqlSchema';

const convertSimplePredicate = (
  predicate: ISimplePredicateFieldsFragment,
  schemaId: string,
): NonNullable<IPredicateInput['simplePredicate']> => ({
  field: predicate.field.key,
  relOp: predicate.relOp.key,
  operands: {
    type:
      predicate.operands.__typename === 'FieldOperands'
        ? OperandType.Field
        : OperandType.Value,
    fieldOperands:
      predicate.operands.__typename === 'FieldOperands'
        ? {
            fieldKeys: predicate.operands.fields.map((field) => field.key),
            schemaId,
          }
        : null,
    valueOperands:
      predicate.operands.__typename === 'ValueOperands'
        ? {
            values: predicate.operands.values.map((value) => {
              if (value.__typename === 'BasicFieldValue') {
                return {
                  type: FieldValueType.Basic,
                  valueKey: value.key,
                };
              } else if (value.__typename === 'AnyFieldValue') {
                // AnyFieldValue case
                return {
                  type: FieldValueType.Any,
                  valueKey: null,
                };
              } else {
                throw new UnreachableCaseError(value);
              }
            }),
          }
        : null,
  },
  matchCase: predicate?.matchCase ?? true,
});

export const convertPredicate = (
  predicate: IPredicateFieldsFragment,
  schemaId: string,
): IPredicateInput => {
  if (predicate.__typename === 'SimplePredicate') {
    return {
      simplePredicate: convertSimplePredicate(predicate, schemaId),
      arrayObjectPredicate: null,
    };
  } else if (predicate.__typename === 'ArrayObjectPredicate') {
    return {
      simplePredicate: null,
      arrayObjectPredicate: {
        elementPredicates: predicate.elementPredicates.map((elementPredicate) =>
          convertSimplePredicate(elementPredicate, schemaId),
        ),
      },
    };
  } else {
    throw new UnreachableCaseError(predicate);
  }
};

// converts a list of logical operators into a list of LogicalOperatorKey
const mapLogicOpKeys = (logicOps: ILogicalOperator[]): LogicalOperatorKey[] =>
  logicOps.map((logOp) => logOp.key);

// takes in a Basic / Named Filter and converts it to IFilterInput
// which will be used to create a filter in the backend
export const parseFilterToInput = (
  filter: IFilterFieldsFragment,
  schemaId: string,
): IFilterInput => {
  if (filter.__typename === 'NamedFilter') {
    return {
      type: FilterType.Named,
      namedFilter: {
        namedFilterId: filter.id,
      },
      basicFilter: null,
    };
  }

  const basicFilter: IBasicFilterInput = {
    // convert a list of ICondition into IConditionInput
    conditions: filter.conditions.map((condition) => {
      const { predicates, logicOps } = condition;

      const newPredicates = predicates.map((predicate) =>
        convertPredicate(predicate, schemaId),
      );

      return {
        predicates: newPredicates,
        logicOps: logicOps.length > 0 ? mapLogicOpKeys(logicOps) : null,
      };
    }),
    logicOps: null,
  };

  if (filter?.logicOps?.length > 0) {
    basicFilter.logicOps = mapLogicOpKeys(filter.logicOps);
  }

  return {
    type: FilterType.Basic,
    namedFilter: null,
    basicFilter,
  };
};
